import { GeneralTag } from '@atoms';
import { ProductNode, Promotion } from '@datamodel';
import { useLanguage } from '@languageProvider';
import { Colors, Mixins, Spacing, Typography } from '@styles';
import { useTheme } from '@themeProvider';
import { parsePrice } from '@utils/helpers';
import React from 'react';
import { ImageBackground, Pressable, Text, View } from 'react-native';

export const ProductNodeCard = ({ product, offers = {}, couponAvailable, style, numberInCart = 0, ...props }) => {
    const { useColor, useElevation } = useTheme();
    const { translate } = useLanguage();
    const [title, label, primaryText, body, backgroundColor, secondary, primary] = useColor([
        'title',
        'label',
        'primary',
        'body',
        'background',
        'secondary',
        ['primary', { isText: true }]]);
    const [numberColor] = useColor([
        ['title', { on: primary }]
    ])

    const soldOut = ProductNode.isSoldOut(product);
    const { promotionId: appliedOffer, price: finalPrice } = Promotion.calculateBestOffer(offers, product.uiPrice);

    return (
        <Pressable {...props} disabled={soldOut} style={[style, Mixins.HORIZONTAL]}>

            <ImageBackground
                style={{
                    width: ProductNodeCard.HEIGHT,
                    height: ProductNodeCard.HEIGHT,
                    ...Mixins.rounding(ProductNodeCard.HEIGHT),
                    // backgroundColor: inputBackground,
                    overflow: 'hidden'
                }}
                source={{ uri: product.photo }}
                resizeMode='contain'
            />

            {
                !!soldOut &&
                <View style={{
                    position: 'absolute',
                    width: ProductNodeCard.HEIGHT,
                    height: ProductNodeCard.HEIGHT,
                    backgroundColor: Colors.setOpacity(label, .5),
                    ...Mixins.rounding(ProductNodeCard.HEIGHT)
                }} />
            }

            <View style={{ marginLeft: Spacing.M, flex: 1, marginRight: Spacing.XL, marginTop: Spacing.XXS }}>
                <Text style={[Typography.TITLE, { color: soldOut ? label : title }]}>{product.name}</Text>
                <Text style={{ textAlignVertical: 'bottom' }}>
                    {
                        !!product.fromFlag &&
                        <Text style={[Typography.SMALL_COPY, { color: label }]}>{translate('from')} </Text>
                    }
                    <Text style={[Typography.TITLE, { color: soldOut ? label : primaryText }]}>{parsePrice(finalPrice, '€')}  </Text>
                    {!!appliedOffer &&
                        <Text style={[Typography.TITLE, { color: label, textDecorationLine: 'line-through' }]}>{parsePrice(product.uiPrice, '€')}</Text>
                    }
                </Text>
                {
                    !!product.description &&
                    <Text style={[Typography.BODY, { color: body }]}>{product.description}</Text>
                }
                <View style={[Mixins.HORIZONTAL, { flexWrap: 'wrap' }]}>
                    {
                        !!soldOut &&
                        <GeneralTag
                            dense
                            style={{ alignSelf: 'flex-start', marginTop: Spacing.XXS, marginRight: Spacing.XXS }}
                            displayText={translate('soldOut')}
                            type="transparent"
                            color={title} />
                    }
                    {
                        !soldOut && !!couponAvailable &&
                        <GeneralTag
                            icon="discount"
                            dense
                            style={{ alignSelf: 'flex-start', marginTop: Spacing.XXS, marginRight: Spacing.XXS }}
                            displayText={translate('couponAvailable')}
                            type="transparent"
                            color={secondary} />
                    }
                    {
                        !soldOut && product.popular &&
                        <GeneralTag
                            dense
                            style={{ alignSelf: 'flex-start', marginTop: Spacing.XXS, marginRight: Spacing.XXS }}
                            displayText="Popular"
                            type="transparent"
                            color={primary} />
                    }
                </View>

            </View>

            {numberInCart >= 1 &&
                <View
                    style={{
                        borderWidth: 2,
                        borderColor: backgroundColor,
                        width: Typography.SMALL_COPY.lineHeight * 1.3,
                        height: Typography.SMALL_COPY.lineHeight * 1.3,
                        ...Mixins.rounding(Typography.SMALL_COPY.lineHeight * 1.3),
                        backgroundColor: primary,
                        justifyContent: 'center',
                        position: 'absolute',
                        left: ProductNodeCard.HEIGHT - 3 / 4 * 1.3 * Typography.SMALL_COPY.lineHeight,
                        top: ProductNodeCard.HEIGHT - 3 / 4 * 1.3 * Typography.SMALL_COPY.lineHeight,
                        // top: - Typography.SMALL_COPY.lineHeight * 1.3 / 4,
                    }}
                >
                    <Text style={[Typography.STRONG, { textAlign: 'center', color: numberColor, fontSize: Typography.SMALL_COPY.fontSize }]}>{numberInCart}</Text>
                </View>
            }

        </Pressable>
    )


}
ProductNodeCard.HEIGHT = 88;


export const ProductItemCard = ({ product, offers = {}, couponAvailable, style, numberInCart = 0, ...props }) => {
    const { useColor, useElevation } = useTheme();
    const elevation = useElevation(1);
    const { translate } = useLanguage();
    const [title, label, primaryText, body, backgroundColor, secondary, primary, line] = useColor([
        'title',
        'label',
        'primary',
        'body',
        'background',
        'secondary',
        ['primary', { isText: true }],
        'line']);
    const [numberColor] = useColor([
        ['title', { on: primary }]
    ])

    const soldOut = ProductNode.isSoldOut(product);

    const { promotionId: appliedOffer, price: finalPrice } = Promotion.calculateBestOffer(offers, product.uiPrice);

    const borderRadius = Mixins.rounding(ProductItemCard.HEIGHT).borderRadius
    return (
        <Pressable {...props}
            disabled={soldOut}
            style={[
                style,
                Mixins.HORIZONTAL,
                !soldOut ? elevation : {}, {
                    borderWidth: !soldOut ? 1 : 0,
                    borderColor: Colors.setOpacity(line, .25),
                    borderRadius,
                    overflow: 'hidden',
                }]}>

            <ImageBackground
                style={{
                    width: ProductItemCard.HEIGHT,
                    height: ProductItemCard.HEIGHT,
                    ...Mixins.radius([borderRadius, 0, 0, borderRadius]),
                    overflow: 'hidden',
                    borderRightWidth: 1,
                    borderColor: line,
                }}
                source={{ uri: product.photo }}
                resizeMode='contain'
            />

            {
                !!soldOut &&
                <View style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: Colors.setOpacity(label, .5),
                    ...Mixins.radius([borderRadius, 0, 0, borderRadius]),
                }} />
            }

            <View style={{ marginHorizontal: Spacing.M, flex: 1, justifyContent: 'center' }}>
                <Text numberOfLines={product.name.split(' ').length > 1 ? 2 : 1} style={[Typography.SMALL_TITLE, { color: soldOut ? label : title }]}>{product.name}</Text>
            </View>

            {numberInCart >= 1 &&
                <View
                    style={{
                        borderWidth: 2,
                        borderColor: backgroundColor,
                        width: Typography.SMALL_COPY.lineHeight * 1.3,
                        height: Typography.SMALL_COPY.lineHeight * 1.3,
                        ...Mixins.rounding(Typography.SMALL_COPY.lineHeight * 1.3),
                        backgroundColor: primary,
                        justifyContent: 'center',
                        position: 'absolute',
                        left: ProductItemCard.HEIGHT - 3 / 4 * 1.3 * Typography.SMALL_COPY.lineHeight,
                        top: ProductItemCard.HEIGHT - 3 / 4 * 1.3 * Typography.SMALL_COPY.lineHeight,
                        // top: - Typography.SMALL_COPY.lineHeight * 1.3 / 4,
                    }}
                >
                    <Text style={[Typography.STRONG, { textAlign: 'center', color: numberColor, fontSize: Typography.SMALL_COPY.fontSize }]}>{numberInCart}</Text>
                </View>
            }

        </Pressable>
    )


}
ProductItemCard.HEIGHT = 44;

export const FungibleCard = ({ style, product, options, offers = {}, coupons = {}, cartCount = {}, displayModal, ...props }) => {
    let PCard = props.isPackItem ? ProductItemCard : ProductNodeCard;
    return (<>
        {!options || options.length === 0 ?
            <PCard
                offers={offers}
                couponAvailable={Object.keys(coupons).length > 0}
                numberInCart={cartCount[product.nodeId]}
                onPress={() => displayModal(product)}
                product={product}
                style={style}
                {...props} />
            :
            <>
                {options.map(option => (
                    <PCard
                        offers={offers}
                        couponAvailable={Object.keys(coupons).length > 0}
                        numberInCart={cartCount[option.nodeId]}
                        onPress={() => displayModal(option)}
                        style={style}
                        product={option}
                        key={option.nodeId} />
                ))}
            </>
        }
    </>)
}
